import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Building2, X, Search, Filter, Power } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ENTITY_STATUS_META } from '@/lib/statusColors';

// Map status → meta + icon. Colours come from the shared palette so every
// page (Approvals, EntitiesLive, Overview…) uses the SAME distinct hex
// per status — no more "two reds" / "two greys".
const ICONS = {
  active: CheckCircle2,
  audited: CheckCircle2,
  warning: AlertTriangle,
  in_arrears: AlertTriangle,
  dividend_overdue: AlertTriangle,
  closing: Power,
  pending: Building2,
  dormant: Building2,
  dissolved: X,
};
const STATUS_META = Object.fromEntries(
  Object.entries(ENTITY_STATUS_META).map(([k, v]) => [k, { ...v, icon: ICONS[k] || Building2 }])
);

const ALL_STATUSES = Object.keys(STATUS_META);
const NEXT_STATUS_OPTIONS = ['active', 'audited', 'warning', 'in_arrears', 'dividend_overdue', 'closing', 'dormant'];

function fmtMoney(n) {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(0);
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + filter effects. */
export default function EntitiesLive() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [entities, setEntities] = useState([]);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [remarks, setRemarks] = useState('');
  const [newStatus, setNewStatus] = useState('active');

  const load = async () => {
    try {
      const { data } = await api.get('/v2/entities/');
      setEntities(data);
    } catch { toast.error('Could not load entities'); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = entities;
    if (statusFilter !== 'all') arr = arr.filter((e) => (e.status || 'active') === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((e) =>
        (e.legal_name || '').toLowerCase().includes(q) ||
        (e.trade_name || '').toLowerCase().includes(q) ||
        (e.cr_number || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [entities, search, statusFilter]);

  const counts = useMemo(() => {
    const c = {};
    entities.forEach((e) => { const s = e.status || 'active'; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [entities]);

  const openDrawer = (ent) => {
    setActive(ent);
    setNewStatus(ent.status || 'active');
    setRemarks(ent.remarks || '');
  };

  const applyStatus = async () => {
    if (!active) return;
    try {
      const { data } = await api.post(`/v2/entities/${active.entity_id}/status-change`, {
        status: newStatus,
        remarks,
      });
      if (data.workflow_triggered) {
        toast.success('Closure requires approval — change request created.');
        nav(`/dashboard/approvals/${data.request_id}`);
      } else {
        toast.success(`Status updated → ${newStatus.replace(/_/g, ' ')}`);
        setActive(null);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change status');
    }
  };

  // Position entities on a 2D grid by status sector (status determines column band)
  // For visual rhythm — calculate gridTemplateColumns based on screen
  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Live Entity Map</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">Every entity. At a glance.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Color-coded by status. Click any entity to drill in or update its status. Closing requires approval through the workflow engine.</p>
        </div>
      </div>

      {/* Status legend — flex-wrap so chips size to their label and never overflow on tablet/zoom */}
      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => setStatusFilter('all')} data-testid="filter-all"
          className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-[11px] uppercase tracking-[0.12em] font-bold whitespace-nowrap transition-all
            ${statusFilter === 'all' ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'}`}>
          All <span className="mono text-[10px] opacity-70">{entities.length}</span>
        </button>
        {ALL_STATUSES.map((s) => {
          const meta = STATUS_META[s];
          const isActive = statusFilter === s;
          return (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
              className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-[11px] uppercase tracking-[0.12em] font-bold whitespace-nowrap transition-all
                ${isActive ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'}`}
              style={isActive ? { background: meta.color, borderColor: meta.color, color: '#fff' } : {}}>
              <span className="h-2 w-2 rounded-full shrink-0" style={{background: meta.color}}/>
              <span>{meta.label}</span>
              <span className="mono opacity-70 text-[10px]">{counts[s] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex items-center gap-3 max-w-md">
        <Search size={14} className="text-slate-400"/>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or CR…" data-testid="live-search" className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
        <span className="text-xs text-slate-500 mono">{filtered.length}/{entities.length}</span>
      </div>

      {/* Live grid map */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {filtered.length === 0 && (
          <div className="col-span-full ve-card p-10 text-center">
            <Building2 size={28} className="mx-auto text-slate-300 mb-3"/>
            <h3 className="display text-xl font-bold">No entities match this filter</h3>
          </div>
        )}
        {filtered.map((e) => {
          const meta = STATUS_META[e.status || 'active'] || STATUS_META.active;
          const Icon = meta.icon;
          const hasWarning = ['warning', 'in_arrears', 'dividend_overdue'].includes(e.status);
          return (
            <button key={e.entity_id} onClick={() => openDrawer(e)} data-testid={`tile-${e.entity_id}`}
              className="group relative aspect-square rounded-xl overflow-hidden text-left transition-all hover:scale-[1.03] hover:shadow-xl"
              style={{
                background: `linear-gradient(135deg, ${meta.color}22 0%, ${meta.color}08 100%)`,
                border: `1.5px solid ${meta.color}`,
              }}>
              <div className="absolute top-2 right-2 h-2 w-2 rounded-full animate-pulse" style={{background: meta.color, boxShadow: `0 0 12px ${meta.color}`}}/>
              {hasWarning && (
                <div className="absolute top-2 left-2 text-rose-700">
                  <AlertTriangle size={14}/>
                </div>
              )}
              <div className="h-full flex flex-col justify-between p-3">
                <Icon size={20} style={{color: meta.color}} strokeWidth={1.8}/>
                <div>
                  <div className="text-[12px] font-bold leading-tight text-slate-900 line-clamp-2">{e.legal_name}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] mt-1.5 ve-tile-label" style={{color: meta.color}}>{meta.label}</div>
                  {e.remarks && (
                    <div className="text-[10px] text-slate-500 mt-1 line-clamp-1 italic">“{e.remarks}”</div>
                  )}
                  <div className="text-[10px] text-slate-500 mono mt-1">{fmtMoney(e.total_invested)} QAR</div>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1" style={{background: meta.color}}/>
            </button>
          );
        })}
      </div>

      {/* Drill-down drawer */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && setActive(null)}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto p-7 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="ve-eyebrow">{(active.legal_type)} · {active.jurisdiction}</div>
                <h2 className="display text-2xl font-bold mt-1">{active.legal_name}</h2>
              </div>
              <button onClick={() => setActive(null)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Current status</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="h-2 w-2 rounded-full" style={{background: STATUS_META[active.status || 'active']?.color}}/>
                  <span className="font-medium capitalize">{(STATUS_META[active.status || 'active']?.label) || active.status}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Equity</div>
                <div className="mt-1 mono">{active.our_equity_percentage?.toFixed(1)}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Invested</div>
                <div className="mt-1 mono">{fmtMoney(active.total_invested)} QAR</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Liabilities</div>
                <div className="mt-1 mono">{fmtMoney(active.outstanding_liabilities)} QAR</div>
              </div>
            </div>

            {active.remarks && (
              <div className="mt-5 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm">
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-bold">Remarks</div>
                <div className="mt-1 text-amber-900">{active.remarks}</div>
              </div>
            )}

            <div className="mt-7 border-t border-slate-200 pt-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">Change status</div>
              <div className="grid grid-cols-2 gap-2">
                {NEXT_STATUS_OPTIONS.map((s) => {
                  const meta = STATUS_META[s];
                  return (
                    <button key={s} onClick={() => setNewStatus(s)} data-testid={`set-status-${s}`}
                      className={`px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-bold rounded-md border transition-colors flex items-center gap-2 ${newStatus === s ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'}`}>
                      <span className="h-2 w-2 rounded-full" style={{background: meta.color}}/>
                      {meta.label}
                    </button>
                  );
                })}
              </div>
              <label className="block mt-4 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Remarks (optional)
                <textarea data-testid="status-remarks" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 font-normal normal-case"/>
              </label>
              {newStatus === 'closing' && (
                <div className="mt-3 p-3 rounded-md bg-rose-50 border border-rose-200 text-xs text-rose-800">
                  ⚠ Closing an entity will create a 3-step approval workflow (Legal → CFO/CEO → Chairman).
                </div>
              )}
              <div className="mt-5 flex gap-2">
                <button onClick={applyStatus} data-testid="apply-status" className="ve-btn-primary text-xs px-4 py-2">Apply</button>
                <Link to={`/dashboard/entities-os/${active.entity_id}`} data-testid="drilldown-link" className="ve-btn-secondary text-xs px-4 py-2">Full profile →</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
